import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/dictionary")({
	component: DictionaryPage,
});

const FIELD_ROWS = [
	{
		layer: "API Contract",
		zh: "客户问题类型",
		field: "intent",
		page: "意图分类 / M02",
		api: "/api/v1/messages",
		entity: "test_cases.expected_intent",
		sensitive: "否",
		meaning: "系统判断客户在问什么，是方案匹配和评测运营的入口。",
	},
	{
		layer: "Field Dictionary",
		zh: "处理方案 ID",
		field: "playbook_id",
		page: "处理方案 / M03",
		api: "/admin/v1/playbook/list",
		entity: "playbooks.playbook_id",
		sensitive: "否",
		meaning: "针对客户问题选择的客服处理流程。",
	},
	{
		layer: "Field Dictionary",
		zh: "可用工具",
		field: "allowed_skills",
		page: "处理方案 / 工具与动作",
		api: "/admin/v1/playbook/detail",
		entity: "playbooks.allowed_skills",
		sensitive: "否",
		meaning: "当前方案允许调用的只读或写入能力。",
	},
	{
		layer: "Event Contract",
		zh: "证据引用",
		field: "evidence_refs",
		page: "提示词与话术 / 评测运营",
		api: "/api/v1/messages",
		entity: "traces.events",
		sensitive: "否",
		meaning: "最终回复引用了哪些工具结果、知识证据或方案依据。",
	},
	{
		layer: "Field Dictionary",
		zh: "Shopify 授权范围",
		field: "shopify_scopes",
		page: "工具与动作 / 接入状态",
		api: "/admin/v1/skills/oauth-health",
		entity: "merchant_configs.shopify_scopes",
		sensitive: "是",
		meaning: "判断商品、订单、客户和写操作是否可读写。",
	},
	{
		layer: "API Contract",
		zh: "应答运行流式事件",
		field: "/admin/v1/runs/stream",
		page: "链路测试台 / P2",
		api: "/admin/v1/runs/stream",
		entity: "AdminRunResponse.events",
		sensitive: "否",
		meaning: "以 NDJSON 增量返回运行事件，支撑运行中观测和最终完成快照。",
	},
	{
		layer: "Event Contract",
		zh: "运行事件类型",
		field: "AdminRunEvent.event_type",
		page: "链路测试台",
		api: "/admin/v1/runs*",
		entity: "Trace.events -> AdminRunEvent",
		sensitive: "否",
		meaning:
			"锁定 run_started 到 run_completed 的 13 类标准事件，避免当前运行和 Trace 回放顺序漂移。",
	},
	{
		layer: "Event Contract",
		zh: "纳入历史轮次",
		field: "history_turns_included",
		page: "链路测试台 / 运营分析",
		api: "/api/v1/messages、/admin/v1/runs*",
		entity: "conversation_io.history_turns_included",
		sensitive: "否",
		meaning: "表示后端实际纳入回复判断的 PG 会话历史轮次，不等同于请求传入 history 条数。",
	},
	{
		layer: "API Contract",
		zh: "数据模式",
		field: "data_mode",
		page: "链路测试台 / 接入状态",
		api: "/admin/v1/runs",
		entity: "AdminRunRequest.data_mode",
		sensitive: "否",
		meaning:
			"标识本次 Admin 运行来自 mock、sandbox、real_read 或 real_write，后续用于评测和审计分层。",
	},
];

const CONTRACT_ROWS = [
	{
		name: "API Contract",
		scope: "/api/v1/messages、/admin/v1/runs*",
		status: "P7A 基线",
		meaning: "锁请求、响应、空值和生成类型，是前后端联调的真实接口合同。",
	},
	{
		name: "Event Contract",
		scope: "AdminRunEvent、Trace replay",
		status: "顺序冻结",
		meaning: "锁事件类型、阶段归属和 Current/Replay 一致顺序，是运行回放顺序的骨架。",
	},
	{
		name: "Field Dictionary",
		scope: "页面字段、实体字段、运营口径",
		status: "V2 扩展",
		meaning: "锁中文名、来源对象、敏感边界和使用场景，避免后续分析口径发散。",
	},
];

const CONSISTENCY_ROWS = [
	{
		field: "runtime_capabilities",
		zh: "运行真实度",
		loop: "观测 / 判断",
		source: "MessagesResponse.runtime_capabilities",
		trace: "Trace.version_snapshot / TraceDetail.version_snapshot.runtime_capabilities",
		ui: "应答测试、运行记录、接入状态",
		evalBadcase: "用于判断样本结果是否代表真实能力，并解释旧运行记录缺字段",
		status: "最小回放已补",
		next: "长期审计与归因",
	},
	{
		field: "data_mode",
		zh: "数据模式",
		loop: "感知 / 观测",
		source: "应答运行请求、评测运行请求",
		trace: "运行开始事件里的 data_mode",
		ui: "应答测试输入、评测运营运行记录",
		evalBadcase: "用于区分 mock、sandbox、真实只读和真实写入",
		status: "部分对齐",
		next: "长期审计和非管理台运行口径",
	},
	{
		field: "run_event.field_refs",
		zh: "事件字段来源",
		loop: "观测",
		source: "/admin/v1/runs*",
		trace: "长期运行记录 -> 当前运行事件投影",
		ui: "应答测试 Inspector",
		evalBadcase: "用于解释单轮运行中每个判断来自哪些字段",
		status: "已对齐",
		next: "继续随事件合同维护",
	},
	{
		field: "TraceEvalSummary",
		zh: "运行评测摘要",
		loop: "判断 / 对比效果",
		source: "/admin/v1/trace/{trace_id}",
		trace: "TraceDetail.eval_summary",
		ui: "运行记录、评测运营详情",
		evalBadcase: "关联评测运行、问题样本、样本期望和关闭建议",
		status: "已对齐",
		next: "S5/S6 扩展到更多评测维度",
	},
	{
		field: "responsible_asset",
		zh: "责任资产",
		loop: "归因 / 执行",
		source: "单样本结果、问题样本、评测样本结果",
		trace: "运行记录只间接通过评测摘要关联",
		ui: "评测运营问题闭环",
		evalBadcase: "用于定位意图规则、方案、工具、知识或话术责任",
		status: "待补证据",
		next: "S4/S6 补资产版本和修复链路",
	},
	{
		field: "asset_change_audit",
		zh: "资产变更审计",
		loop: "执行 / 对比效果",
		source: "前端最小治理口径；现有资产仅有分域版本字段",
		trace: "运行记录版本快照只能看到部分版本；持久化审计日志待补",
		ui: "提示词与话术审计链路、处理方案、知识与证据后续共用",
		evalBadcase: "用于解释改了哪个资产、为什么改、是否进入本次对比",
		status: "流程口径已补，持久化待做",
		next: "建立后端审计实体和自动版本归因",
	},
] as const;

const CONSISTENCY_ISSUES = [
	"运行记录详情已补 runtime_capabilities 最小展示；旧记录未记录时仍显示“未记录”，不能倒推为真实运行。",
	"data_mode 不等同真实运行开关；新的应答运行记录可回放，旧记录和非管理台运行仍可能未知。",
	"资产变更审计已补前端治理流程；后端持久化、自动版本归因和长期责任链仍待补。",
];

const FIELD_SYNC_RULES = [
	{
		trigger: "接口或 schema 变化",
		fact: "OpenAPI / TS 类型",
		mustUpdate: "api-contracts/09_schema_field_index.md、P4_field_api_dictionary.md",
		productUpdate: "字段矩阵、产品指南、实施日志",
		doneEvidence: "E2E 命中页面字段；doc-view marker 命中",
	},
	{
		trigger: "运行记录 / 当前运行事件字段变化",
		fact: "后端 schema、运行记录回放、事件注册表",
		mustUpdate: "api-contracts/04_field_event_contract.md、P7B 观测矩阵",
		productUpdate: "P8P9_S3_field_consistency_governance.md、应答测试说明",
		doneEvidence: "当前运行和历史回放字段口径一致",
	},
	{
		trigger: "评测 / 问题样本字段变化",
		fact: "评测运行、评测样本结果、问题样本、运行评测摘要",
		mustUpdate: "api-contracts/03_trace_eval_health_api.md、P4_field_api_dictionary.md",
		productUpdate: "评测运营说明、字段一致性矩阵",
		doneEvidence: "问题样本或评测运行能追溯来源、中文解释和页面位置",
	},
	{
		trigger: "页面展示字段变化",
		fact: "Admin UI 字段、表格列、Inspector 展示",
		mustUpdate: "字段字典、产品升级使用指南",
		productUpdate: "P8P9_implementation_log.md、docs/CHANGELOG.md、doc-view",
		doneEvidence: "页面展示、字段解释和文档入口一致",
	},
] as const;

const FIELD_GAP_TASKS = [
	{
		id: "FIELD-GAP-001",
		title: "运行真实度回放",
		impact:
			"新运行记录已能说明本轮是真 LLM、static、Qdrant、dry-run 还是 blocked；旧记录仍需按未记录解释。",
		owner: "最小展示已完成；剩余进入长期归因",
		status: "最小回放已补",
		acceptance: "运行记录和应答测试回放看到同一份运行真实度；旧记录显示未记录。",
	},
	{
		id: "FIELD-GAP-002",
		title: "data_mode 长期审计口径",
		impact: "产品可能误以为数据模式会强切 /api/v1/messages runtime，仍需在长期审计里持续解释。",
		owner: "最小口径已补；剩余进入历史口径",
		status: "部分对齐",
		acceptance: "页面能区分数据口径、真实运行能力和对比可信度；长期趋势仍回到 S6。",
	},
	{
		id: "FIELD-GAP-003",
		title: "asset_change_audit 最小版本责任链",
		impact:
			"已补最小流程：先记录变更意图、绑定责任资产和版本、跑单环节测试、跑端到端和评测、写回复盘结论。",
		owner: "最小流程已补；后端审计实体、自动版本归因和长期责任链仍待补",
		status: "流程口径已补，持久化待做",
		acceptance: "提示词、处理方案、工具和知识的关键变更先有前端检查链路，后续再实体化。",
	},
] as const;

const STATUS_ROWS = [
	{ raw: "read-only", zh: "只读查询", meaning: "只读取事实，不修改订单或客户数据。" },
	{ raw: "write", zh: "写入操作", meaning: "可能修改订单，必须有保护策略和审计。" },
	{ raw: "not_configured", zh: "未配置", meaning: "当前环境没有接入该外部服务。" },
	{ raw: "sandbox_only", zh: "仅沙盒", meaning: "只能证明 contract 和页面流程，不影响真实店铺。" },
	{ raw: "handoff", zh: "转人工", meaning: "系统无法可靠自动处理，需要人工接手。" },
];

function fieldFamilyAnchor(field: string) {
	return `field-family-${field.replace(/[^a-zA-Z0-9_-]+/g, "-")}`;
}

function DictionaryPage() {
	const [query, setQuery] = useState("");
	const filteredRows = useMemo(() => {
		const normalized = query.trim().toLowerCase();
		if (!normalized) return FIELD_ROWS;
		return FIELD_ROWS.filter((row) =>
			[row.layer, row.zh, row.field, row.page, row.api, row.entity, row.meaning].some((value) =>
				value.toLowerCase().includes(normalized),
			),
		);
	}, [query]);

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="P4"
				title="接口与字段"
				description="把接口文档、页面字段、API 字段、数据实体和中文解释统一起来，减少口径偏差。"
				help={dictionaryHelp}
			/>

			<section className="space-y-3">
				<h3 className="text-sm font-semibold text-text-primary">合同覆盖</h3>
				<div className="grid gap-3 lg:grid-cols-3">
					{CONTRACT_ROWS.map((row) => (
						<Card key={row.name}>
							<CardHeader className="pb-3">
								<CardTitle className="text-base">{row.name}</CardTitle>
								<CardDescription>{row.scope}</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="flex items-center justify-between gap-3">
									<span className="text-sm font-medium text-text-primary">覆盖状态</span>
									<span className="rounded-md bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary">
										{row.status}
									</span>
								</div>
								<p className="mt-3 text-sm leading-6 text-text-secondary">{row.meaning}</p>
							</CardContent>
						</Card>
					))}
				</div>
			</section>

			<Card data-testid="field-consistency-matrix">
				<CardHeader>
					<CardTitle className="text-lg">字段一致性治理矩阵</CardTitle>
					<CardDescription>
						按
						API、运行记录、当前运行事件、页面、评测和问题样本六个位置核对同一字段，先锁住最小证据链。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-3 md:grid-cols-3">
						<ConsistencyMetric label="字段族" value={`${CONSISTENCY_ROWS.length} 组`} />
						<ConsistencyMetric label="当前重点" value="runtime / data / 资产" />
						<ConsistencyMetric label="后续承接" value="地图互链 / 审计链" />
					</div>
					<div className="overflow-x-auto">
						<table className="w-full min-w-[1180px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">字段族</th>
									<th className="px-3 py-2 font-medium">闭环作用</th>
									<th className="px-3 py-2 font-medium">API / 事实入口</th>
									<th className="px-3 py-2 font-medium">运行记录 / 事件口径</th>
									<th className="px-3 py-2 font-medium">页面位置</th>
									<th className="px-3 py-2 font-medium">评测 / 问题样本用途</th>
									<th className="px-3 py-2 font-medium">当前判断</th>
									<th className="px-3 py-2 font-medium">下一步</th>
								</tr>
							</thead>
							<tbody>
								{CONSISTENCY_ROWS.map((row) => (
									<tr
										key={row.field}
										id={fieldFamilyAnchor(row.field)}
										data-testid="field-consistency-row"
										className="border-t border-surface-border align-top"
									>
										<td className="px-3 py-3">
											<div className="font-medium text-text-primary">{row.zh}</div>
											<div className="mt-1 font-mono text-xs text-text-muted">{row.field}</div>
										</td>
										<td className="px-3 py-3 text-text-secondary">{row.loop}</td>
										<td className="max-w-[210px] px-3 py-3 font-mono text-xs leading-5 text-text-secondary">
											{row.source}
										</td>
										<td className="max-w-[230px] px-3 py-3 text-text-secondary">{row.trace}</td>
										<td className="max-w-[210px] px-3 py-3 text-text-secondary">{row.ui}</td>
										<td className="max-w-[230px] px-3 py-3 text-text-secondary">
											{row.evalBadcase}
										</td>
										<td className="px-3 py-3">
											<span className={consistencyStatusClass(row.status)}>{row.status}</span>
										</td>
										<td className="max-w-[180px] px-3 py-3 text-text-secondary">{row.next}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			<section className="grid gap-4 lg:grid-cols-[1fr_0.75fr]">
				<Card data-testid="field-consistency-issue-panel">
					<CardHeader>
						<CardTitle className="text-lg">当前不包装成已完成</CardTitle>
						<CardDescription>这三项会影响后续观测、评测和归因，先透明暴露。</CardDescription>
					</CardHeader>
					<CardContent>
						<ul className="space-y-2 text-sm leading-6 text-text-secondary">
							{CONSISTENCY_ISSUES.map((issue) => (
								<li
									key={issue}
									className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2"
								>
									{issue}
								</li>
							))}
						</ul>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">验收口径</CardTitle>
						<CardDescription>这一阶段只做入口和矩阵，不替代后续字段自动化审计。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3 text-sm leading-6 text-text-secondary">
						<p>任一字段必须能追到中文解释、事实源、页面位置、评测或问题样本用途和下一步。</p>
						<p>如果当前只有规划合同或间接证据，页面直接标为缺口或待补证据。</p>
						<p>后续再处理文档同步规则和必要接口/前端小修。</p>
					</CardContent>
				</Card>
			</section>

			<Card data-testid="field-sync-rulebook">
				<CardHeader>
					<CardTitle className="text-lg">字段更新同步规则</CardTitle>
					<CardDescription>
						先改事实源，再改说明文档；字段变化必须能回到接口、运行记录、页面和产品验收口径。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm leading-6 text-text-secondary">
						已建立同步门和任务清单；运行真实度已补最小回放；字段与地图互链已接入，剩余长期审计后续推进。
					</div>
					<div className="overflow-x-auto">
						<table className="w-full min-w-[1050px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">触发场景</th>
									<th className="px-3 py-2 font-medium">先改事实源</th>
									<th className="px-3 py-2 font-medium">必须同步</th>
									<th className="px-3 py-2 font-medium">产品/日志</th>
									<th className="px-3 py-2 font-medium">完成证据</th>
								</tr>
							</thead>
							<tbody>
								{FIELD_SYNC_RULES.map((rule) => (
									<tr
										key={rule.trigger}
										data-testid="field-sync-rule-row"
										className="border-t border-surface-border align-top"
									>
										<td className="px-3 py-3 font-medium text-text-primary">{rule.trigger}</td>
										<td className="px-3 py-3 text-text-secondary">{rule.fact}</td>
										<td className="max-w-[260px] px-3 py-3 font-mono text-xs leading-5 text-text-secondary">
											{rule.mustUpdate}
										</td>
										<td className="max-w-[240px] px-3 py-3 text-text-secondary">
											{rule.productUpdate}
										</td>
										<td className="max-w-[240px] px-3 py-3 text-text-secondary">
											{rule.doneEvidence}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			<Card data-testid="field-gap-task-board">
				<CardHeader>
					<CardTitle className="text-lg">字段缺口任务化</CardTitle>
					<CardDescription>
						把 S3-A 暴露出的关键缺口落到后续切片，不把待补字段留在口头说明里。
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="mb-3 rounded-md border border-surface-border bg-surface-subtle px-3 py-3 text-sm leading-6 text-text-secondary">
						S3-B 负责把缺口任务化；S3-C 已收口最小 Trace 回放；S3-D 负责页面互链，剩余进入
						S6，不改变 Agent 主链路。
					</div>
					<div className="grid gap-3 lg:grid-cols-3">
						{FIELD_GAP_TASKS.map((task) => (
							<div
								key={task.id}
								data-testid="field-gap-task-row"
								className="rounded-md border border-surface-border bg-surface-background p-4"
							>
								<div className="flex items-start justify-between gap-3">
									<div>
										<div className="font-mono text-xs font-medium text-text-muted">{task.id}</div>
										<h3 className="mt-1 text-sm font-semibold leading-5 text-text-primary">
											{task.title}
										</h3>
									</div>
									<span className={gapTaskStatusClass(task.status)}>{task.status}</span>
								</div>
								<p className="mt-3 text-sm leading-6 text-text-secondary">{task.impact}</p>
								<div className="mt-3 space-y-2 text-xs leading-5 text-text-muted">
									<p>
										<span className="font-medium text-text-secondary">承接：</span>
										{task.owner}
									</p>
									<p>
										<span className="font-medium text-text-secondary">验收：</span>
										{task.acceptance}
									</p>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">字段检索</CardTitle>
					<CardDescription>支持搜合同层级、中文、英文字段、页面、接口或实体名。</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<label className="relative block">
						<Search
							className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
							aria-hidden="true"
						/>
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="搜索中文、英文、页面或接口"
							className="h-10 w-full rounded-md border border-surface-border bg-surface-background pl-9 pr-3 text-sm"
						/>
					</label>
					<div className="overflow-x-auto">
						<table className="w-full min-w-[980px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">合同层级</th>
									<th className="px-3 py-2 font-medium">中文名</th>
									<th className="px-3 py-2 font-medium">英文字段</th>
									<th className="px-3 py-2 font-medium">页面</th>
									<th className="px-3 py-2 font-medium">接口</th>
									<th className="px-3 py-2 font-medium">实体字段</th>
									<th className="px-3 py-2 font-medium">敏感</th>
									<th className="px-3 py-2 font-medium">业务解释</th>
								</tr>
							</thead>
							<tbody>
								{filteredRows.map((row) => (
									<tr key={row.field} className="border-t border-surface-border align-top">
										<td className="px-3 py-3 text-text-secondary">{row.layer}</td>
										<td className="px-3 py-3 font-medium text-text-primary">{row.zh}</td>
										<td className="px-3 py-3 font-mono text-text-secondary">{row.field}</td>
										<td className="px-3 py-3 text-text-secondary">{row.page}</td>
										<td className="px-3 py-3 font-mono text-text-secondary">{row.api}</td>
										<td className="px-3 py-3 font-mono text-text-secondary">{row.entity}</td>
										<td className="px-3 py-3 text-text-secondary">敏感: {row.sensitive}</td>
										<td className="max-w-sm px-3 py-3 text-text-secondary">{row.meaning}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			<section className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">状态值对照</CardTitle>
						<CardDescription>避免把英文枚举直接暴露给产品和运营。</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-2 md:grid-cols-2">
							{STATUS_ROWS.map((row) => (
								<div
									key={row.raw}
									className="rounded-md border border-surface-border bg-surface-background p-3"
								>
									<div className="flex items-center justify-between gap-3">
										<span className="font-mono text-sm text-text-primary">{row.raw}</span>
										<span className="rounded-md bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary">
											{row.zh}
										</span>
									</div>
									<p className="mt-2 text-sm leading-6 text-text-secondary">{row.meaning}</p>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">字典使用规则</CardTitle>
						<CardDescription>页面说明引用字段字典，不复制多份解释。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3 text-sm leading-6 text-text-secondary">
						<p>页面说明只写当前页面常用字段；全局解释、接口来源和实体关系统一回到字段字典。</p>
						<p>测试集 expected 字段、链路测试台输入输出、API 联调说明都应引用这里的中文名。</p>
						<p>涉及订单、客户、token、scope 的字段必须标明敏感属性和真实数据边界。</p>
					</CardContent>
				</Card>
			</section>
		</div>
	);
}

function ConsistencyMetric({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3">
			<div className="text-xs font-medium text-text-muted">{label}</div>
			<div className="mt-1 text-base font-semibold text-text-primary">{value}</div>
		</div>
	);
}

function consistencyStatusClass(status: (typeof CONSISTENCY_ROWS)[number]["status"]) {
	const base = "inline-flex rounded-md px-2 py-1 text-xs font-medium";
	if (status === "已对齐" || status === "最小回放已补" || status === "流程口径已补，持久化待做")
		return `${base} bg-brand-success/10 text-brand-success`;
	if (status === "待补证据" || status === "部分对齐")
		return `${base} bg-brand-warning/10 text-brand-warning`;
	return `${base} bg-brand-danger/10 text-brand-danger`;
}

function gapTaskStatusClass(status: (typeof FIELD_GAP_TASKS)[number]["status"]) {
	const base = "shrink-0 rounded-md px-2 py-1 text-xs font-medium";
	if (status === "部分对齐") return `${base} bg-brand-warning/10 text-brand-warning`;
	if (status === "最小回放已补" || status === "流程口径已补，持久化待做")
		return `${base} bg-brand-success/10 text-brand-success`;
	return `${base} bg-surface-subtle text-text-secondary`;
}

const dictionaryHelp: PageHelp = {
	title: "字段字典说明",
	description: "这里是字段解释的统一入口，用来对齐中文展示、英文字段、接口和数据实体。",
	sections: [
		{
			title: "谁会用",
			items: [
				"产品用它确认页面字段含义，避免一个概念多个名字。",
				"运营用它理解测试集期望值和失败原因。",
				"研发和 QA 用它对齐 API 字段、实体字段和页面展示。",
			],
		},
		{
			title: "维护原则",
			items: [
				"新增页面字段时，先判断是否已有字典项。",
				"新增接口字段时，补中文名、页面、实体、示例和敏感标记。",
				"状态枚举必须有中文展示，不能只暴露英文值。",
			],
		},
	],
};
